import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { PDFDocumentProxy } from 'pdfjs-dist';

/** Local rendering: no third-party viewer receives the signed document URL. */
export function PdfPreview({ fileUrl, fileName }: { fileUrl: string; fileName: string }) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let cancelled = false;
    let task: ReturnType<typeof import('pdfjs-dist').getDocument> | undefined;
    setPdf(null); setPage(1); setError(''); setLoading(true);
    void (async () => {
      try {
        const lib = await import('pdfjs-dist');
        const { default: worker } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        if (cancelled) return;
        lib.GlobalWorkerOptions.workerSrc = worker;
        const assets = `/pdfjs/${lib.version}/`;
        task = lib.getDocument({
          url: fileUrl,
          wasmUrl: `${assets}wasm/`,
          cMapUrl: `${assets}cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `${assets}standard_fonts/`,
          // Decoder failures must surface rather than silently omitting fax images.
          stopAtErrors: true,
        });
        const document = await task.promise;
        if (!cancelled) {
          const requestedPage = Number(fileUrl.match(/#page=(\d+)(?:&|$)/)?.[1] ?? 1);
          setPage(Math.max(1, Math.min(document.numPages, requestedPage)));
          setPdf(document);
        }
      } catch {
        if (!cancelled) { setError('PDF preview could not be loaded. Use Open in new tab or Download to review the original.'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; void task?.destroy(); };
  }, [fileUrl]);
  useEffect(() => {
    if (!pdf || !canvas.current) return;
    let cancelled = false;
    let rendering: ReturnType<Awaited<ReturnType<PDFDocumentProxy['getPage']>>['render']> | undefined;
    setLoading(true); setError('');
    void (async () => {
      try {
        const source = await pdf.getPage(page);
        if (cancelled || !canvas.current) return;
        const viewport = source.getViewport({ scale: zoom * 1.5 });
        const target = canvas.current;
        target.width = viewport.width; target.height = viewport.height;
        rendering = source.render({ canvas: target, viewport });
        await rendering.promise;
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) { setError('This page could not be displayed. Open the original document to review it.'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; rendering?.cancel(); };
  }, [pdf, page, zoom]);
  return <div className="flex flex-col gap-3">
    {pdf && <div className="flex flex-wrap items-center gap-2" aria-label="PDF page controls">
      <Button variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous page</Button>
      <span>Page {page} of {pdf.numPages}</span>
      <Button variant="outline" disabled={page >= pdf.numPages || loading} onClick={() => setPage(p => p + 1)}>Next page</Button>
      <Button variant="outline" disabled={zoom <= 1 || loading} onClick={() => setZoom(z => z - 0.5)}>Zoom out</Button>
      <Button variant="outline" disabled={zoom >= 3 || loading} onClick={() => setZoom(z => z + 0.5)}>Zoom in</Button>
    </div>}
    {loading && <p role="status">Loading PDF page…</p>}
    {error && <p role="alert">{error}</p>}
    <div className="max-h-[70vh] overflow-auto rounded-md border bg-muted/30">
      <canvas ref={canvas} role="img" aria-label={`${fileName}, page ${page}`} className={loading || error ? 'hidden' : 'block'} style={{ maxWidth: zoom === 1 ? '100%' : undefined, height: 'auto' }} />
    </div>
  </div>;
}
